/* =============================================================================
   VIEWS / CUSTOMER-PLAN — Gast-Wunschtisch auf dem Raumplan (S204 Cut 3)
   =============================================================================
   Ersetzt im Gast-Reservierungsformular (index.html) das „Bereich (Wunsch)"-
   Dropdown durch ein Plan-Widget:
     · 3er-Segment „Egal · Innen · Außen · Club" (nur aktivierte Bereiche) IST
       die Bereichswahl → speist das versteckte #r-area (Egal-Default = any).
     · darunter der Raum: Struktur-Elemente statisch (core/layout-render) +
       datensparsame Tisch-Kacheln (nur Name · „bis X Pers." · frei/belegt).
     · Tisch-Tipp = OPTIONALE, UNVERBINDLICHE Anfrage (→ #r-wish-table); erneuter
       Tipp wählt ab. Große Gruppen: alle zu klein = gewollt, Hinweis statt Sperre.

   Belegt-Status kommt datensparsam über den anon-RPC public_busy_tables (NUR
   table_id, keine PII). Non-destruktiv: Änderungen rendern nur den Widget-Inhalt
   (#customer-plan-inner) neu. Inline-Handler CSP: func(), kein window.func().
============================================================================ */

import { htmlEscape } from '../utils/helpers.js';
import { renderElementStatic, elementsForArea, canvasSizeForArea, planCanvasHtml, contentBBox } from '../core/layout-render.js';
import { fitAllPlans, sizePlanPanel } from '../utils/plan-fit.js';
import {
  enabledPlanAreas, segmentToArea, tableMaxPersons, tileStatus,
  visiblePlanTables, noSingleTableFits, toggleWish, revalidateWish
} from '../core/table-plan.js';

let _planArea = null;      // aktives Segment (null = „Egal"/any)
let _wishTableId = null;   // unverbindlicher Wunschtisch
let _wishHint = '';        // S208 — dezenter Hinweis, wenn der Wunsch re-validiert verworfen wurde
let _busySet = new Set();  // belegte/verplante table_id's im aktuellen Slot
let _busyDebounce = null;
let _busyToken = 0;        // Race-Guard für async Busy-Loads

function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { tables: [], settings: {}, layoutElements: [], layoutAreas: [] };
}
function currentGuests() { return parseInt(document.getElementById('r-guests')?.value, 10) || 0; }
function currentDate()   { return document.getElementById('r-date')?.value || ''; }
function currentTime()   { return document.getElementById('r-time')?.value || ''; }

/** Angezeigter Bereich: aktives Segment, sonst erster verfügbarer (indoor). */
function displayArea() {
  if (_planArea) return _planArea;
  const areas = enabledPlanAreas(getState().settings);
  return areas.length ? areas[0].id : 'indoor';
}

function _syncHidden() {
  const areaEl = document.getElementById('r-area');
  if (areaEl) areaEl.value = segmentToArea(_planArea);
  const wishEl = document.getElementById('r-wish-table');
  if (wishEl) wishEl.value = _wishTableId || '';
}

function _tableById(id) {
  return (getState().tables || []).find(t => t.id === id) || null;
}

/**
 * S208 — Wunsch gegen die AKTUELLE Personenzahl + Belegung re-validieren. Passt der
 * gesetzte Wunschtisch nicht mehr (zu klein oder jetzt belegt), wird er verworfen und
 * ein dezenter Hinweis gesetzt (statt still stehenzubleiben). Gibt true zurück, wenn
 * der Wunsch entfernt wurde. Kein Wunsch → No-Op (Hinweis bleibt bis der Gast agiert).
 */
function _revalidateWish() {
  if (!_wishTableId) return false;
  const t = _tableById(_wishTableId);
  const res = revalidateWish(t, _busySet, currentGuests());
  if (res.keep) { _wishHint = ''; return false; }
  const name = (t && t.name) || _wishTableId;
  const g = currentGuests();
  _wishHint = res.reason === 'busy'
    ? `Wunschtisch ${name} ist für diesen Slot nicht mehr frei — bitte neu wählen.`
    : `Wunschtisch ${name} passt nicht mehr für ${g} ${g === 1 ? 'Person' : 'Personen'} — bitte neu wählen.`;
  _wishTableId = null;
  _syncHidden();
  return true;
}

/** Maximal datensparsame Kachel: AUSSCHLIESSLICH „XP" (max. Personen) — KEIN
 *  Tischname, kein Status-Text (Jonas-Entscheid, emphatisch). Frei/belegt/zu-klein
 *  und die Wunsch-Auswahl laufen komplett über Farbe + Auswählbarkeit. Der
 *  Titel-Tooltip trägt „bis X Pers." für Hover/Barrierefreiheit (kein Name). */
export function _renderTile(t, guests) {
  const st = tileStatus(t, _busySet, guests);
  const isWish = _wishTableId === t.id;
  const maxP = tableMaxPersons(t);
  // HOTFIX 2: Geometrie-Klassen sind die Single-Source (identisch zu Editor +
  // Belegungs-Snapshot: table-node + seats-N + shape-X). KEINE Inline-Maße im
  // Gast-Pfad → die CSS-Regeln liefern Größe/Form; guest-spezifisch bleiben nur
  // die cp-*-Status-/Wunsch-Klassen und der „XP"-Inhalt.
  const cls = `table-node seats-${Math.min(12, Number(t.seats) || 1)} shape-${t.shape || 'rectangular'} cp-tile cp-${st.status}${isWish ? ' cp-wish' : ''}`;
  const click = st.selectable ? `onclick="custPlanToggleWish('${htmlEscape(t.id)}')"` : '';
  return `
    <div class="${cls}" style="left:${Number(t.x) || 0}px; top:${Number(t.y) || 0}px; cursor:${st.selectable ? 'pointer' : 'default'};"
         ${click} title="bis ${maxP} Pers." aria-label="Tisch bis ${maxP} Personen"
         aria-disabled="${st.selectable ? 'false' : 'true'}">
      <div class="cp-cap">${maxP}P</div>
    </div>`;
}

/** Raum-Canvas (Struktur-Elemente statisch + Tisch-Kacheln) — im Popup gezeigt. */
function renderPlanCanvas(area, guests) {
  const state = getState();
  const size = canvasSizeForArea(state, area);
  const els = elementsForArea(state, area);
  const elLayer = els.map(renderElementStatic).join('');
  const tables = visiblePlanTables(state.tables, area);
  const tilesHtml = tables.length
    ? tables.map(t => _renderTile(t, guests)).join('')
    : `<p class="tc-hint" style="padding:14px; color:var(--ink-soft);">Keine Tische in diesem Bereich.</p>`;
  // S207-HOTFIX 3+4 — fit-skalierbarer Canvas, auf den Inhalt gecroppt (kein toter Rand).
  const bbox = contentBBox(els, tables, 24) || { x: 0, y: 0, w: size.w, h: size.h };
  return planCanvasHtml({
    canvasW: bbox.w, canvasH: bbox.h, cropX: bbox.x, cropY: bbox.y,
    outerClass: 'cust-plan-scroll', innerClass: 'cust-plan-canvas',
    innerHtml: elLayer + tilesHtml
  });
}

/**
 * Innerer Widget-Inhalt (kompakt): NUR das Segment + eine schlanke Zeile.
 * „Egal" = eingeklappt (kein Plan). Bei gewähltem Bereich erscheint ein Button,
 * der den Raum im Popup öffnet (Jonas-UX: entschlackt die Erstansicht). Die
 * Wunsch-Zusammenfassung wird hier gezeigt, sobald ein Tisch gewählt ist.
 */
export function renderCustomerPlanInner() {
  const state = getState();
  const guests = currentGuests();
  const areas = enabledPlanAreas(state.settings);

  const seg = `
    <div class="occ-seg cust-plan-seg" role="tablist" aria-label="Bereich (Wunsch)">
      <button type="button" class="occ-seg-btn${_planArea === null ? ' active' : ''}" role="tab"
        aria-selected="${_planArea === null}" onclick="custPlanSetSegment('')">Egal</button>
      ${areas.map(a => `<button type="button" class="occ-seg-btn${_planArea === a.id ? ' active' : ''}" role="tab"
        aria-selected="${_planArea === a.id}" onclick="custPlanSetSegment('${a.id}')">${htmlEscape(a.label)}</button>`).join('')}
    </div>`;

  // „Egal" → eingeklappt: kein Plan, nur ein ruhiger Hinweis.
  if (_planArea === null) {
    return `${seg}
      <div class="cust-plan-hint">Wähle optional einen Bereich, um einen Wunschtisch anzusehen — sonst schlagen wir dir passend einen Tisch vor.</div>`;
  }

  const bigGroup = noSingleTableFits(state.tables, _planArea, guests);
  if (bigGroup) {
    return `${seg}
      <div class="cust-plan-hint cust-plan-hint-group">Für ${guests} Personen stellen wir Tische für euch zusammen — eine Tischwahl ist hier nicht nötig.
        <button type="button" class="cust-plan-openbtn cust-plan-openbtn-ghost" onclick="custPlanOpenModal()">Raum ansehen</button></div>`;
  }

  // S208 — dezenter Re-Validierungs-Hinweis (nur ohne aktiven Wunsch anzeigen).
  const warnRow = (_wishHint && !_wishTableId)
    ? `<div class="cust-plan-hint cust-plan-wish-warn">⚠ ${htmlEscape(_wishHint)}</div>`
    : '';

  const wishRow = _wishTableId
    ? `<div class="cust-plan-hint cust-plan-wish">💺 Wunschtisch: <strong>${htmlEscape((_tableById(_wishTableId) || {}).name || _wishTableId)}</strong> — wir versuchen, ihn freizuhalten.
        <button type="button" class="cust-plan-openbtn" onclick="custPlanOpenModal()">ändern</button>
        <button type="button" class="cust-plan-clear" onclick="custPlanToggleWish('${htmlEscape(_wishTableId)}')">entfernen</button></div>`
    : `<div class="cust-plan-hint">
        <button type="button" class="cust-plan-openbtn" onclick="custPlanOpenModal()">🪑 Raum ansehen &amp; Wunschtisch wählen</button>
        <span style="margin-left:8px;">optional — unverbindliche Anfrage.</span></div>`;

  return `${seg}${warnRow}${wishRow}`;
}

/** Nur den Widget-Inhalt neu rendern (non-destruktiv, Rest des Formulars bleibt). */
function _rerender() {
  const inner = document.getElementById('customer-plan-inner');
  if (inner) inner.innerHTML = renderCustomerPlanInner();
  _rerenderModal();
}

// ---------------------------------------------------------------------------
// Popup (Raum-Ansicht) — self-contained Overlay, unabhängig vom Cockpit-Modal.
// ---------------------------------------------------------------------------
let _modalOpen = false;

function _ensureModalHost() {
  let host = document.getElementById('cust-plan-modal');
  if (!host) {
    host = document.createElement('div');
    host.id = 'cust-plan-modal';
    host.className = 'cust-plan-modal-overlay';
    host.addEventListener('click', (e) => { if (e.target === host) custPlanCloseModal(); });
    document.body.appendChild(host);
  }
  return host;
}

function _modalBodyHtml() {
  const state = getState();
  const area = displayArea();
  const guests = currentGuests();
  const label = (enabledPlanAreas(state.settings).find(a => a.id === area) || {}).label || 'Raum';
  const wishLine = _wishTableId
    ? `💺 <strong>${htmlEscape((_tableById(_wishTableId) || {}).name || _wishTableId)}</strong> gewählt`
    : 'Tippe optional einen freien Tisch an (unverbindlich).';
  return `
    <div class="cust-plan-modal-panel" role="dialog" aria-modal="true" aria-label="Raum ${htmlEscape(label)}">
      <div class="cust-plan-modal-head">
        <strong>${htmlEscape(label)} — Wunschtisch</strong>
        <button type="button" class="modal-close" onclick="custPlanCloseModal()" aria-label="Schließen">×</button>
      </div>
      <div class="cust-plan-modal-sub">${wishLine}</div>
      ${renderPlanCanvas(area, guests)}
      <div class="cust-plan-modal-foot">
        ${_wishTableId ? `<button type="button" class="cust-plan-clear" onclick="custPlanToggleWish('${htmlEscape(_wishTableId)}')">Auswahl entfernen</button>` : '<span></span>'}
        <button type="button" class="btn btn-primary btn-sm" onclick="custPlanCloseModal()">Fertig</button>
      </div>
    </div>`;
}

// S207-HOTFIX 5 — Popup-Panel-Breite folgt der gecroppten Skizze (min 420 fürs
// Gast-Popup-Chrome, max wie Modal). Segment-Wechsel zieht sie live nach.
const _GUEST_PANEL_OPTS = { minW: 420, maxWpx: 1100, maxWvw: 92, chrome: 44 };
function _sizeGuestPanel(host) {
  if (host) sizePlanPanel(host.querySelector('.cust-plan-modal-panel'), _GUEST_PANEL_OPTS);
}

function _rerenderModal() {
  if (!_modalOpen) return;
  const host = document.getElementById('cust-plan-modal');
  if (host) { host.innerHTML = _modalBodyHtml(); _sizeGuestPanel(host); fitAllPlans(host); }
}

/** Raum-Popup öffnen (nur bei gewähltem Bereich). Belegt-Set frisch ziehen. */
export function custPlanOpenModal() {
  if (_planArea === null) return;
  _modalOpen = true;
  const host = _ensureModalHost();
  host.innerHTML = _modalBodyHtml();
  host.classList.add('open');
  document.body.classList.add('cust-plan-modal-lock');
  _sizeGuestPanel(host);       // Panel-Breite an die gecroppte Skizze anpassen …
  fitAllPlans(host);           // … dann Canvas einpassen (nach .open = sichtbar)
  refreshCustomerPlanBusy();   // aktuelle Belegung → Kacheln aktualisieren sich
}

/** Raum-Popup schließen + Widget-Zusammenfassung aktualisieren. */
export function custPlanCloseModal() {
  _modalOpen = false;
  const host = document.getElementById('cust-plan-modal');
  if (host) { host.classList.remove('open'); host.innerHTML = ''; }
  document.body.classList.remove('cust-plan-modal-lock');
  const inner = document.getElementById('customer-plan-inner');
  if (inner) inner.innerHTML = renderCustomerPlanInner();
}

/** Segment/Bereich wählen. '' = Egal (any). Wechsel verwirft einen Wunsch, der
 *  nicht mehr zum Bereich passt. Speist #r-area + refresht die Availability-Preview. */
export function custPlanSetSegment(seg) {
  _wishHint = '';   // S208 — Bereichswechsel = User-Aktion → Re-Validierungs-Hinweis weg
  _planArea = (seg === 'indoor' || seg === 'outdoor' || seg === 'club') ? seg : null;
  // „Egal" = wirklich keine Präferenz → Wunsch verwerfen (Ansicht klappt ein).
  // Bereichswechsel: Wunsch verwerfen, wenn der Tisch nicht mehr zum Bereich passt.
  if (_planArea === null) {
    _wishTableId = null;
    custPlanCloseModal();
  } else if (_wishTableId) {
    const t = _tableById(_wishTableId);
    if (!t || t.area !== _planArea) _wishTableId = null;
  }
  _syncHidden();
  _rerender();
  refreshCustomerPlanBusy();               // Bereich gewechselt → Kacheln/Belegt neu
  if (typeof window.refreshAvailabilityPreview === 'function') window.refreshAvailabilityPreview();
}

/** Wunschtisch an-/abwählen (nur freie Kacheln; erneuter Tipp wählt ab). */
export function custPlanToggleWish(tableId) {
  const t = _tableById(tableId);
  if (!t) return;
  const st = tileStatus(t, _busySet, currentGuests());
  if (!st.selectable && _wishTableId !== tableId) return;  // belegt/zu klein: nicht wählbar
  _wishHint = '';                                          // S208 — User agiert → Hinweis weg
  _wishTableId = toggleWish(_wishTableId, tableId);
  _syncHidden();
  _rerender();
}

/** Belegt-Set (public_busy_tables) für den aktuellen Slot neu laden — debounced
 *  wie die Availability-Preview (~80 ms). Race-sicher via Token. */
export function refreshCustomerPlanBusy() {
  clearTimeout(_busyDebounce);
  _busyDebounce = setTimeout(async () => {
    const date = currentDate(), time = currentTime();
    const token = ++_busyToken;
    let ids = [];
    if (date && time) {
      try {
        const api = window.App && window.App.storage && window.App.storage.reservations;
        if (api && typeof api.busyTables === 'function') ids = await api.busyTables(date, time);
      } catch (_) { ids = []; }
    }
    if (token !== _busyToken) return;        // veralteter Load → verwerfen
    _busySet = new Set(ids || []);
    // S208 — Wunsch gegen aktuelle Personenzahl + Belegung re-validieren (zu klein
    // ODER belegt → verwerfen + dezenter Hinweis, statt still stehenzubleiben).
    _revalidateWish();
    _rerender();
  }, 80);
}

/** Erst-Mount ins Formular (ersetzt das #r-area-Dropdown). */
export function mountCustomerPlan() {
  const inner = document.getElementById('customer-plan-inner');
  if (!inner) return;
  _syncHidden();
  inner.innerHTML = renderCustomerPlanInner();
  refreshCustomerPlanBusy();
}

export function getWishTableId() { return _wishTableId || null; }

/** Nach erfolgreichem Submit: Widget zurücksetzen (Egal, kein Wunsch). */
export function resetCustomerPlan() {
  _planArea = null; _wishTableId = null; _wishHint = ''; _busySet = new Set();
  custPlanCloseModal();
  _syncHidden();
  _rerender();
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    custPlanSetSegment, custPlanToggleWish, mountCustomerPlan,
    refreshCustomerPlanBusy, resetCustomerPlan,
    custPlanOpenModal, custPlanCloseModal
  });
}
